(ns arthur.ui.pool
  "The media pool: what can be put into the open symbol, in two scopes.

  THIS PROJECT is the open document's own: every symbol in it — the open one
  included, because none is special — and the video it uses or was given this
  session. ALL ASSETS is everything the server holds: every upload, and every
  symbol of every other saved project. Whole projects are `ui/openmenu`'s, and
  the split is the point: opening a project REPLACES what is on screen, where
  everything in here is a thing to put INTO it.

  TABS RATHER THAN TWO OPEN FOLDERS. Both scopes expanded cost eight lines of
  heading before the first row, in a pane 210px wide; you are either looking at
  what the document has or shopping what the server has, and the one case where
  that costs you is covered — a search counts its hits in the scope you are not
  looking at and puts the number on its tab.

  THE MAIN TIMELINE LEADS, and it is `clip/opens-on`'s answer rather than a
  name: the symbol the project plays and the one the work happens in, so it gets
  the top of the pane and a row drawn at a size you can read a pose off. Every
  other symbol is the tree under it: what nothing places beside it at the top,
  and the rest under whatever places it — see `this-project`. None of it is
  `:main` being special either: rename it, place it inside something else, and
  the pool follows the document.

  Every row is a drag source, and what it carries says what it is:
  `symbol:<id>` for a symbol of this document, `import:<project>|<cid>|<id>` for
  one of another's, and `footage:<id>` for video. The stage and the timeline are
  where they are dropped.

  A SOUND — mp3, wav — is `sound:<id>`, and is dropped on the timeline, where it
  becomes an audio node in the open symbol from the frame it lands on.

  RENAMING IS TWO DIFFERENT OPERATIONS behind one affordance. A symbol's name is
  a field of this document: it is an undoable edit, and blank gives it back its
  id. Footage and sounds live beside projects rather than inside one, so their
  label is a server write shared by every project that uses the row — see
  `events.footage/relabel`. A symbol of ANOTHER project is renameable where it
  lives and not from here.

  ADDING IS AT THE FOOT: a new empty symbol, which goes into the library and is
  placed nowhere until it is dragged, and an upload, which is the same as a file
  dropped on the pane.

  THE WHOLE PANE IS THE DROP TARGET for a file. A video dropped anywhere in it
  uploads and lands in THIS PROJECT's media, and goes no further: which frames of
  it become a symbol, and what that symbol is called, is asked when it is dropped
  where it should go. The uploaded bytes, the extraction job and the decoded
  footage stay separate records on the server, so dropping the same file twice
  does not decode it twice."
  (:require [arthur.domain.clip :as clip]
            [arthur.domain.palette :as pal]
            [arthur.domain.raster :as raster]
            [arthur.events.footage :as footage]
            [arthur.events.playback :as pb]
            [arthur.events.project :as project]
            [arthur.events.ui :as ui]
            [arthur.subs.playback :as playback]
            [arthur.subs.render :as render]
            [arthur.subs.ui :as sub]
            [arthur.ui.canvas :as canvas]
            [arthur.ui.drag :as drag]
            [arthur.ui.tracing :as tracing]
            [clojure.string :as str]
            [re-frame.core :as rf]
            [reagent.core :as r]))

;; ---------------------------------------------------------------------------
;; pictures

(defonce ^:private thumbs
  ;; `{:clip <the document these were drawn from> :urls {sid url-or-nil}}`,
  ;; compared by IDENTITY. A document is an immutable value, so the same object
  ;; means the same pictures and a different one means an edit landed — at which
  ;; point the whole map goes rather than being diffed, because the cheap test is
  ;; the wrong one: a symbol unchanged in itself changes picture when any symbol
  ;; it places changes, and following that is `clip/resolver`'s job, not a
  ;; cache's.
  (atom {:clip nil :urls {}}))

(defn- draw-symbol
  "`sid`'s first frame as a PNG data URL, through the resolver and the rasteriser
  the stage uses — so the picture in the pool is the picture, not a sketch of it.

  ROOTED AT THE SYMBOL, which is only honest because a symbol carries its own
  placement. A face's geometry is head-local in units of one image height, and
  the source-to-stage mapping that turns those into stage pixels lives on the
  face's own `:place` — see `freeze/face-placement`. So a face drawn by itself is
  the size it is on the stage, and this needs to know nothing about where it
  happens to be used.

  nil rather than a throw when the frame will not resolve — a cycle, a missing
  block, an instance naming a symbol that has gone. A pool is a list of names,
  and a symbol that cannot be drawn today is still one you want listed, named
  and draggable.

  THE FIRST FRAME, unlike `video-thumb` below, which seeks past the black leader
  most phone footage opens on. A symbol's frame 0 is authored: what is on it is
  there because somebody put it there, and the frame a person names a symbol by
  is the one it starts on."
  [document sid store palette]
  (try
    (when (clip/symbol document sid)
      (let [resolve (clip/resolver document sid store palette {})
            [w h]   (clip/stage document sid)]
        (let [ops (resolve 0)]
          (-> (raster/make w h)
              (raster/clear! pal/bg-slot)
              (raster/draw-ops! ops)
              (canvas/->png (pal/effective-ramp palette (clip/active-palette resolve)))))))
    (catch :default _ nil)))

(defn- symbol-thumb
  "`draw-symbol`, once per symbol per version of the document. Rasterising and
  PNG-encoding the stage is milliseconds, and the pool redraws on every selection
  click."
  [document sid store palette]
  (when-not (identical? document (:clip @thumbs))
    (reset! thumbs {:clip document :urls {}}))
  (let [urls (:urls @thumbs)]
    (if (contains? urls sid)
      (get urls sid)
      (let [url (draw-symbol document sid store palette)]
        (swap! thumbs assoc-in [:urls sid] url)
        url))))

(defn- picture
  "A row's leading picture. One box at the stage's own proportions for all three
  kinds of row, so the names line up down the list whatever is beside them, and
  a row with no picture to show still holds the column open."
  [url]
  (if url
    [:img.thumb {:src url :alt "" :draggable false}]
    [:span.thumb]))

(defn- video-thumb
  "One frame of a video, from its proxy. `#t=` seeks a paused, muted element to a
  frame that is not the black leader most phone footage opens on; nothing is
  played and nothing is decoded past it."
  [{:keys [video]}]
  (if video
    ;; Sized here as well as in the stylesheet: a video element with no size
    ;; is as big as its footage, and a portrait phone clip is 1440×1920.
    [:video.thumb {:src (str video "#t=0.2") :muted true :preload "metadata"
                   :plays-inline true :tab-index -1
                   :style {:width 32 :height 20 :max-width 32 :max-height 20}}]
    [:span.thumb]))

(defn- tracing-thumb
  "The first source frame, using the stage's media URL cache."
  [media]
  (r/with-let [ready (r/atom 0)]
    @ready
    [picture (tracing/url-of media 0 #(swap! ready inc))]))

;; ---------------------------------------------------------------------------
;; the row

(defn- carrying
  "The drag handlers for a row. `text` is what makes it a drag at all and what
  another application would receive; `start!` puts the real payload where the
  stage and the timeline can read it while hovering — see `ui/drag`."
  [text start!]
  {:draggable true
   :on-drag-start (fn [^js event]
                    (.setData (.-dataTransfer event) "text/plain" text)
                    (set! (.. event -dataTransfer -effectAllowed) "copy")
                    (start!))
   :on-drag-end (fn [_] (drag/done!))})

(defn- row
  "One row of the pool: a picture, a name, and one number.

  ONE LINE, because the pane is 210px wide and the pane under it is the timeline
  — vertical space spent here is spent on something. The picture carries what the
  row is, the name carries which one, and the single number is the one a person
  needs before dragging it somewhere: how long it is. Everything else the row
  knows goes in `:title`, where a pointer can ask for it.

  THE WHOLE ROW IS THE BUTTON. Clicking selects and double-clicking opens, and
  the target for both is the row — not the name, which is a word a few characters
  wide that you would have to aim at.

  WHICH IS WHY THE PENCIL IS A SPAN. It belongs beside the name, where it reads
  as acting on that word rather than on the row; inside the button is the only
  place that can be, and a button inside a button is not a thing HTML has. A span
  with a click handler is, and it costs one thing: the pencil is not a tab stop.
  F2 on the row is the keyboard path, which is the conventional one anyway, and
  `aria-keyshortcuts` is what announces it. Stopping propagation is what keeps a
  click on the pencil from also selecting the row underneath it.

  `opts` beyond the keys destructured here is merged onto that drag surface, so a
  caller adds its handlers without this growing a parameter per affordance.

  THE MAIN TIMELINE is the one exception, and the only one. Border, fill and size
  each say \"separate thing\", so spending them on every row flattens the list
  into wallpaper; spent on the one row where the work happens, they say so. It
  gets a picture big enough to read a pose off, and both its facts on a line of
  its own under the name — `sub2` — rather than one of them out at the edge.

  `rename` is `{:key :value :begin! :commit!}` and is what makes a row
  renameable at all: a row without one has no pencil and does not answer F2.

  `depth` puts a row in a tree: indented that far, with a twisty ahead of it.
  `inside` is `{:count :open? :flip!}` for a row with rows under it; a row with
  none keeps the twisty's room, so names line up down a level.

  `delete!` puts a cross beside the pencil while the row is picked."
  [{:keys [label sub sub2 title thumb on? open? main? disabled? rename depth inside delete!]
    :as opts}]
  (let [{:keys [key value begin! commit!]} rename
        editing? (boolean (and key (= key (:editing rename))))]
    [:div.pool-row {:class (str (when main? "main ") (when on? "on ")
                                (when open? "open ") (when disabled? "disabled ")
                                (when editing? "editing"))
                    :style (when depth {:padding-left (+ 4 (* 10 depth))})}
     (when depth
       (if-let [{:keys [count open? flip!]} inside]
         [:span.pool-twisty {:title (str count " inside")
                             :on-click (fn [^js e] (.stopPropagation e) (flip!))}
          (if open? "\u25BE" "\u25B8")]
         [:span.pool-twisty]))
     (if editing?
       ;; The input REPLACES the row rather than floating over it, so the list
       ;; does not change height while you type in it.
       [:input.pool-name
        {:auto-focus true
         :default-value value
         :aria-label (str "rename " label)
         :on-focus (fn [^js e] (.select (.-target e)))
         ;; Blur commits, as the project title in `ui/topbar` does: clicking away
         ;; from a half-finished rename means the name you typed, not nothing.
         :on-blur (fn [^js e] (commit! (.. e -target -value)))
         :on-key-down (fn [^js e]
                        (case (.-key e)
                          "Enter" (do (.preventDefault e) (commit! (.. e -target -value)))
                          ;; Escape has to stop editing WITHOUT the blur that
                          ;; follows it committing the draft.
                          "Escape" (do (.preventDefault e) (begin! nil) (.blur (.-target e)))
                          nil))}]
       [:<>
        [:button.pool-item
         (merge {:disabled (boolean disabled?)
                 :title (or title label)
                 :aria-keyshortcuts (when begin! "F2")
                 :on-key-down (when begin!
                                (fn [^js e]
                                  (when (= "F2" (.-key e))
                                    (.preventDefault e)
                                    (begin! key))))}
                (dissoc opts :label :sub :sub2 :title :on? :open? :main?
                        :disabled? :thumb :rename :depth :inside :delete!))
         thumb
         [:span.text
          ;; The label is `.text`'s first child and stays that way: it is how
          ;; the browser test finds a row by the name a person reads.
          [:span.name label]
          (when (and main? sub2) [:span.detail sub2])]
         ;; The main row says both its facts under the name instead.
         (when (and sub (not main?)) [:span.sub sub])
         ;; At the row's end, together, as a timeline row has them: the pencil
         ;; on every row and the cross on what is picked. Two quick clicks on
         ;; either are two clicks, not a double-click that opens the row.
         [:span.pool-actions {:on-double-click (fn [^js e] (.stopPropagation e))}
          (when begin!
            [:span.pool-rename
             {:title (str "rename " label " (F2)")
              ;; Mouse-only by design — see the docstring. A `tabindex` here would
              ;; make it interactive content inside a button, which is the thing
              ;; being avoided, and F2 already reaches it from the keyboard.
              :on-click (fn [^js e] (.stopPropagation e) (begin! key))}
             "\u270E"])
          (when (and delete! on?)
            [:span.pool-rename.pool-delete
             {:title (str "delete " label " (Delete)")
              :on-click (fn [^js e] (.stopPropagation e) (delete!))}
             "\u00D7"])]]])]))

;; ---------------------------------------------------------------------------
;; the rows of each kind

(defonce ^:private anchor (atom nil))

(defn- pick!
  "A click picks `what`, as a click on a timeline row does: with ⌘ it is added
  to the picked or taken out of them, and with shift every row shown from the
  last one clicked to this one is picked."
  [^js e what]
  (let [shown (vec (keep #(aget % "arthurPick")
                         (array-seq (.querySelectorAll (.closest (.-currentTarget e) ".pane.pool")
                                                       ".pool-item"))))
        a (.indexOf shown @anchor)
        b (.indexOf shown what)]
    (cond
      (or (.-metaKey e) (.-ctrlKey e)) (rf/dispatch [::ui/toggle-selection what])
      (and (.-shiftKey e) (<= 0 a) (<= 0 b))
      (rf/dispatch [::ui/select-many (subvec shown (min a b) (inc (max a b)))])
      :else (rf/dispatch [::ui/select what]))
    (when-not (.-shiftKey e) (reset! anchor what))))

(defn- pickable
  "The ref that tells `pick!` which rows are shown, in order."
  [what]
  (fn [^js el] (when el (aset el "arthurPick" what))))

(defn- symbol-row [document sid {:keys [clip-id store palette selection open main rename
                                        depth inside]}]
  (let [sym   (clip/symbol document sid)
        label (clip/symbol-name document sid)
        nodes (count (:nodes sym))]
    ^{:key (str sid)}
    [row (merge {:label label
                 :sub (str (:frames sym) "f")
                 :title (str label " · " (:frames sym) " frames · " nodes
                             (if (= 1 nodes) " node" " nodes")
                             (when (= sid open) " · open")
                             (if (= :trace (:type sym))
                               " — drag to place"
                               " — double-click to open, drag to place"))
                 :thumb (if (= :trace (:type sym))
                          [tracing-thumb (:media sym)]
                          [picture (symbol-thumb document sid store palette)])
                 :sub2 (str (:frames sym) "f · " nodes (if (= 1 nodes) " node" " nodes"))
                 :on? (contains? selection [:symbol sid])
                 :open? (= sid open)
                 :main? (= sid main)
                 :depth depth
                 :inside inside
                 :delete! (when-not (= sid main) #(rf/dispatch [::ui/delete-symbols [sid]]))
                 :rename (assoc rename
                                :key [:symbol sid]
                                :value label
                                :commit! (fn [value]
                                           ((:begin! rename) nil)
                                           (rf/dispatch [::project/rename-symbol sid value])))
                 :on-click #(pick! % [:symbol sid])
                 :ref (pickable [:symbol sid])
                 :on-double-click #(rf/dispatch [::pb/open-symbol sid])}
                (carrying (str "symbol:" (subs (str sid) 1))
                          #(drag/symbol! clip-id sid open)))]))

(defn- palette-transition-row [document sid selection rename]
  (let [sym (clip/symbol document sid)
        p (get (pal/palettes document) (:palette-ref sym))]
    ^{:key (str sid)}
    [row {:label (clip/symbol-name document sid)
          :sub (str "uses " (or (:name p) (:palette-ref sym)))
          :title "Palette transition definition"
          :on? (contains? selection [:symbol sid])
          :rename (assoc rename
                         :key [:symbol sid]
                         :value (clip/symbol-name document sid)
                         :commit! (fn [value]
                                    ((:begin! rename) nil)
                                    (rf/dispatch [::project/rename-symbol sid value])))
          :on-click #(pick! % [:symbol sid])
          :ref (pickable [:symbol sid])}]))

(defn- footage-row [{:keys [id label frames fps video width height] :as f} chosen rename]
  ^{:key id}
  [row (merge {:label label
               :sub (str frames "f")
               :title (str label " · " frames " frames @ " fps "fps"
                           " — drag onto the stage to make a symbol of it")
               :thumb [video-thumb f]
               :on? (= id chosen)
               :rename (assoc rename
                              :key [:footage id]
                              :value label
                              :commit! (fn [value]
                                         ((:begin! rename) nil)
                                         (rf/dispatch [::footage/relabel :footage id value])))
               :on-click #(rf/dispatch [::footage/choose id])}
              (carrying (str "footage:" id)
                        #(drag/other! {:kind :footage :id id :label label
                                       :frames frames :fps fps :video video
                                       :width width :height height})))])

(defn- sound-row
  "An uploaded sound, or with `:footage?` a video's own — which is how a take's
  sound goes back into its symbol after it was deleted there."
  [{:keys [id label duration footage? fps]} project-fps rename]
  (let [frames (js/Math.ceil (* duration project-fps))
        [source length rate] (if footage?
                               [{:footage id :fps fps} (js/Math.round (* duration fps)) 1]
                               [{:sound id :fps project-fps} frames 1])]
    ^{:key (str (when footage? "f") id)}
    [row (merge {:label label
                 :sub (str (.toFixed duration 1) "s")
                 :title (str label " · " (.toFixed duration 1) "s · " frames " frames"
                             (when footage? " · this video's own sound")
                             " — drag onto the timeline")
                 :thumb [:span.thumb.sound "♪"]
                 ;; A video's own sound is named by the video. Renaming it here
                 ;; would rename the footage row two sections up, which is not
                 ;; what the pencil on a sound looks like it does.
                 :rename (when-not footage?
                           (assoc rename
                                  :key [:sound id]
                                  :value label
                                  :commit! (fn [value]
                                             ((:begin! rename) nil)
                                             (rf/dispatch [::footage/relabel :sound id value]))))}
                (carrying (str "sound:" id)
                          #(drag/other! {:kind :sound :source source :label label
                                         :length length :rate rate :frames frames})))]))

(defn- image-row
  "An uploaded still, to be traced over: dropped, it becomes a tracing layer —
  see `events/ui ::drop-tracing`."
  [{:keys [id label width height digest url]} rename]
  ^{:key id}
  [row (merge {:label label
               :sub (str width "×" height)
               :title (str label " · " width "×" height
                           " — drag onto the stage or the timeline to trace over it")
               :thumb [:img.thumb {:src url :alt ""
                                   :style {:width 32 :height 20 :object-fit "cover"}}]
               :rename (assoc rename
                              :key [:image id]
                              :value label
                              :commit! (fn [value]
                                         ((:begin! rename) nil)
                                         (rf/dispatch [::footage/relabel :image id value])))}
              (carrying (str "image:" id)
                        #(drag/other! {:kind :tracing :label label :frames 1
                                       :center [(/ width 2) (/ height 2)]
                                       :symbol {:name label :type :trace
                                                :media {:image digest}
                                                :width width :height height :nodes {}}})))])

(defn- import-row [{:keys [cid symbol name frames]} pid]
  ^{:key (str cid symbol)}
  [row (merge {:label name
               :sub (str frames "f")
               :title (str name " · " frames " frames — drag to copy it into this project")
               :thumb [picture nil]}
              (carrying (str "import:" (str/join "|" [pid cid symbol]))
                        #(drag/other! {:kind :import :label name :frames frames
                                       :project pid :cid cid :symbol symbol})))])

(defn- import-palette-row [{:keys [project palette name] :as asset}]
  ^{:key (str project palette)}
  [row {:label name :sub "palette"
        :title (str name " — click to copy this palette into the project")
        :thumb [picture nil]
        :on-click #(rf/dispatch [::project/import-palette asset])}])

;; ---------------------------------------------------------------------------
;; sections

(defn- hit?
  "Is `label` a hit for `query`? A case-folded substring, which is the whole of
  what a list of a few dozen names needs."
  [query label]
  (or (str/blank? query)
      (str/includes? (str/lower-case (str label)) query)))

(defn- section
  "A heading and its rows.

  NOTHING AT ALL while a search is running and this section has no hit. A column
  of headings over emptiness is the worst thing a filter can show you: the answer
  to \"where is it\" should be the one section still holding rows."
  [{:keys [title rows blank searching?]}]
  (cond
    (seq rows) [:div.pool-section [:h2 title] (into [:div.pool-rows] rows)]
    searching? nil
    :else [:div.pool-section [:h2 title] [:div.dim blank]]))

(defn- sections
  "Draw a scope's sections, or the one line that says a search found nothing in
  it. `rows` is the scope's total so the empty answer can be given once rather
  than per section."
  [searching? rows children]
  (if (and searching? (zero? rows))
    ;; Its own class rather than `.dim`: a `.dim` directly inside `.pane-body`
    ;; is how the page says what loading, saving and opening are doing — see
    ;; the status line at the foot of `view` — and a search result is not that.
    [:div.pool-empty "no match in this scope"]
    (into [:<>] (map section) children)))

(def ^:private own-section?
  "Symbols with a section of their own, kept out of the tree."
  #{:palette :palette-track :trace})

(defn- listed? [document sid]
  (not (own-section? (:type (clip/symbol document sid)))))

(defn- placed-in
  "What each symbol places, as `{owner [sid ...]}`: each once, in the order they
  first start in it."
  [document]
  (reduce-kv (fn [m sid at]
               (if (listed? document sid)
                 (reduce-kv (fn [m owner [[_ f]]] (update m owner (fnil conj []) [f sid]))
                            m (group-by first at))
                 m))
             {} (clip/placements document)))

(defn- tree
  "`sid`'s row and, while it is open, the rows of what it places under it. A
  symbol placed in two symbols is under both. `path` is what this row is under,
  which keys the row and stops a cycle."
  [{:keys [document placed expanded flip! main] :as ctx} sid depth path]
  (let [kids (->> (get placed sid) (sort-by (juxt first (comp str second)))
                  (map second) (remove (set (conj path sid))))
        root? (and (= main sid) (empty? path))
        open? (and (seq kids) (or root? (contains? expanded [path sid])))]
    (into [(with-meta (symbol-row document sid
                                  (assoc ctx :depth depth
                                         :inside (when (and (seq kids) (not root?))
                                                   {:count (count kids) :open? open?
                                                    :flip! #(flip! [path sid])})))
             {:key (str (conj path sid))})]
          (when open?
            (mapcat #(tree ctx % (inc depth) (conj path sid)) kids)))))

(defn- this-project
  "The open document's own: its symbols as the tree of what places what, then
  each kind with a section of its own.

  WHAT NOTHING PLACES IS AT THE TOP, and everything else is under what places
  it. Drawing makes a symbol per drawing, and a flat list of every one buries the
  few that matter; a drawing is under its lane, its lane under its scene. What
  is left at the top is the root the project opens on, drawn big and first with
  what it places always showing under it, and
  whatever is placed nowhere — a scene, something made to be placed later, or
  something left behind — which is what wants looking at: keep it, place it, or
  delete it. Nothing is ever cleared away for you.

  One rule for every symbol, worked out from the document on each render and
  stored nowhere: place it, unplace it, and the pool follows.

  A SEARCH IS FLAT: you asked for it by name, and a hit inside a closed twisty is
  a hit you cannot see."
  [{:keys [document query searching? media sounds images chosen rename main selection] :as ctx}]
  (let [named? #(hit? query (clip/symbol-name document %))
        symbol-ids (sort-by str (keys (:symbols document)))
        transition-ids (into #{} (mapcat (fn [sym]
                                           (when (= :palette-track (:type sym))
                                             (keep (comp :symbol :source val) (:nodes sym)))))
                             (vals (:symbols document)))
        transition? #(or (= :palette (:type (clip/symbol document %)))
                         (contains? transition-ids %))
        transitions (filterv #(and (named? %) (transition? %)) symbol-ids)
        tracing (filterv #(and (named? %) (clip/trace? (clip/symbol document %)))
                         symbol-ids)
        listed (filterv #(listed? document %) symbol-ids)
        symbols (if searching?
                  (mapv #(symbol-row document % ctx) (filterv named? listed))
                  (let [ctx (assoc ctx :placed (placed-in document))
                        top (cond->> (filterv #(and (not= main %)
                                                    (empty? (clip/owners document %)))
                                              listed)
                              (contains? (:symbols document) main) (into [main]))]
                    (into [] (mapcat #(tree ctx % 0 [])) top)))
        media  (filterv #(hit? query (:label %)) media)
        sounds (filterv #(hit? query (:label %)) sounds)
        images (filterv #(hit? query (:label %)) images)]
    [sections searching?
     (+ (count symbols) (count transitions) (count tracing)
        (count media) (count sounds) (count images))
     [{:title "symbols" :searching? searching?
       :blank "nothing to open yet"
       :rows symbols}
      {:title "tracing layers" :searching? searching?
       :blank "no tracing layers yet"
       :rows (mapv #(symbol-row document % ctx) tracing)}
      {:title "palette transitions" :searching? searching?
       :blank "no palette transitions"
       :rows (mapv #(palette-transition-row document % selection rename) transitions)}
      {:title "media" :searching? searching?
       :blank "drop a video here"
       :rows (mapv #(footage-row % chosen rename) media)}
      {:title "sounds" :searching? searching?
       :blank "drop an mp3 or wav here"
       :rows (mapv #(sound-row % (:fps document) rename) sounds)}
      {:title "images" :searching? searching?
       :blank "drop a png or jpeg here to trace over"
       :rows (mapv #(image-row % rename) images)}]]))

(defn- all-assets
  "Everything the server holds. Other projects' symbols stay grouped by project
  and closed: a server holds many, and a wall of every symbol in every one buries
  the one you want."
  [{:keys [document query searching? rename chosen available all-sounds all-images symbols
           palettes project-id]}]
  (let [media   (filterv #(hit? query (:label %)) available)
        sounds  (filterv #(hit? query (:label %)) all-sounds)
        images  (filterv #(hit? query (:label %)) all-images)
        others  (filterv #(and (not= project-id (:project %)) (hit? query (:name %)))
                         symbols)
        grouped (sort-by (comp str second key) (group-by (juxt :project :project-name) others))
        palettes (filterv #(and (not= project-id (:project %)) (hit? query (:name %))) palettes)]
    [sections searching?
     (+ (count media) (count sounds) (count images) (count others) (count palettes))
     [{:title "media" :searching? searching?
       :blank "nothing uploaded yet"
       :rows (mapv #(footage-row % chosen rename) media)}
      {:title "sounds" :searching? searching?
       :blank "no sounds uploaded yet"
       :rows (mapv #(sound-row % (:fps document) rename) sounds)}
      {:title "images" :searching? searching?
       :blank "no images uploaded yet"
       :rows (mapv #(image-row % rename) images)}
      {:title "palettes" :searching? searching?
       :blank "no palettes in other saved projects"
       :rows (mapv import-palette-row palettes)}
      {:title "symbols" :searching? searching?
       :blank "no other saved projects"
       :rows (for [[[pid pname] rows] grouped]
               ^{:key (str pid)}
               ;; Open while a search is running: you asked for these by name,
               ;; and a hit behind a closed twisty is a hit you cannot see.
               [:details.pool-project {:open (boolean searching?)}
                [:summary (str pname " · " (count rows)
                               (if (= 1 (count rows)) " symbol" " symbols"))]
                (into [:div.pool-rows] (map #(import-row % pid)) rows)])}]]))

;; ---------------------------------------------------------------------------

(defn- counts
  "How many rows each scope holds for `query`.

  This is what keeps the tabs from hiding anything. A tab shows its tally only
  while a search is running and only on the scope you are NOT looking at, so the
  one cost of collapsing two folders into two tabs — that a hit could be behind
  the tab you did not pick — is paid off by a number, counted over the same
  labels the rows are filtered by."
  [{:keys [document query media sounds images available all-sounds all-images symbols
           palettes project-id]}]
  (let [n (fn [labels] (count (filter #(hit? query %) labels)))]
    {:project (+ (n (map #(clip/symbol-name document %) (keys (:symbols document))))
                 (n (map :name (vals (pal/palettes document))))
                 (n (map :label media))
                 (n (map :label sounds))
                 (n (map :label images)))
     :assets (+ (n (map :label available))
                (n (map :label all-sounds))
                (n (map :label all-images))
                (n (map :name (remove #(= project-id (:project %)) palettes)))
                (n (map :name (remove #(= project-id (:project %)) symbols))))}))

(defn view []
  (r/with-let [;; Counted, not a boolean. `dragenter`/`dragleave` fire for every
               ;; child element the pointer crosses, so a flag set on enter and
               ;; cleared on leave flickers off the moment the drag passes over a
               ;; row — the depth counter is what makes the outline steady.
               depth (r/atom 0)
               query (r/atom "")
               scope (r/atom :project)
               ;; Which row is being renamed, as `[kind id]`. One atom rather
               ;; than a flag per row: exactly one name is ever being edited, and
               ;; opening a second input has to close the first.
               editing (r/atom nil)
               ;; Which tree rows are open, as `[path sid]`. Everything under
               ;; the root's own children starts shut — see `tree`.
               expanded (r/atom #{})]
    (let [{:keys [loading? status available sounds images uploaded chosen]}
          @(rf/subscribe [::playback/footage])
          document  @(rf/subscribe [::sub/settled-clip])
          clip-id   @(rf/subscribe [::render/clip-id])
          store     @(rf/subscribe [::render/store])
          palette   @(rf/subscribe [::render/palette])
          selection (set @(rf/subscribe [::sub/selections]))
          open      @(rf/subscribe [::render/open])
          media     @(rf/subscribe [::sub/project-footage])
          {:keys [symbols palettes]} @(rf/subscribe [::project/assets])
          {project-id :id} @(rf/subscribe [::playback/project])
          ;; The project's videos' sounds first, then its uploaded ones.
          own-sounds (into (mapv (fn [{:keys [id label frames fps]}]
                                   {:id id :label label :footage? true :fps fps
                                    :duration (/ frames fps)})
                                 media)
                           @(rf/subscribe [::sub/project-sounds]))
          ;; The stills this document traces over, and those uploaded while it
          ;; was open — as `::sub/project-sounds` decides for sounds.
          traced     (into #{} (keep #(get-in % [:media :image])) (vals (:symbols document)))
          own-images (filterv #(or (contains? traced (:digest %))
                                   (contains? (set uploaded) (:id %)))
                              images)
          needle     (str/lower-case (str/trim @query))
          searching? (boolean (seq needle))
          ctx {:document document :clip-id clip-id :store store :palette palette
               :selection selection :open open
               :media media :sounds own-sounds :images own-images :chosen chosen
               :available (vec available) :all-sounds (vec sounds)
               :all-images (vec images)
               :symbols symbols :palettes palettes :project-id project-id
               :query needle :searching? searching?
               ;; `clip/opens-on` and not `:main`: the longest symbol nothing
               ;; else places is the timeline the work happens in, and it is the
               ;; one row in this pane drawn big enough to read a pose off.
               :main (clip/opens-on document)
               :rename {:editing @editing :begin! #(reset! editing %)}
               :expanded @expanded
               :flip! #(swap! expanded (fn [open] (if (open %) (disj open %) (conj open %))))}
          tallies (counts ctx)
          files? (fn [^js e] (some #{"Files"} (array-seq (.. e -dataTransfer -types))))]
      [:section.pane.pool
       {:aria-label "media pool"
        :class (when (pos? @depth) "dropping")
        ;; Only a FILE lights the pane up: a row dragged out of the pool is on
        ;; its way somewhere else.
        :on-drag-enter (fn [^js e] (when (files? e) (.preventDefault e) (swap! depth inc)))
        :on-drag-leave (fn [^js e] (when (files? e) (swap! depth #(max 0 (dec %)))))
        :on-drag-over  (fn [^js e] (when (files? e) (.preventDefault e)))
        :on-drop (fn [^js e]
                   (.preventDefault e)
                   (reset! depth 0)
                   (when-let [file (aget (.. e -dataTransfer -files) 0)]
                     (rf/dispatch [::footage/upload file])))}
       [:div.pane-head "media pool"]
       [:input {:id "pool-file" :type "file" :accept "video/*,audio/*,image/*"
                :style {:display "none"}
                :on-change (fn [^js event]
                             (when-let [file (aget (.. event -target -files) 0)]
                               (rf/dispatch [::footage/upload file])
                               (set! (.. event -target -value) "")))}]
       [:div.pool-find
        [:input.pool-search
         {:id "pool-search" :type "search" :value @query
          :placeholder "search" :aria-label "search the media pool"
          :on-change #(reset! query (.. % -target -value))
          :on-key-down (fn [^js e] (when (= "Escape" (.-key e)) (reset! query "")))}]
        (when searching?
          [:button.pool-clear {:title "clear the search" :aria-label "clear the search"
                               :on-click #(reset! query "")}
           "×"])]
       [:div.tabs
        (doall
         (for [[k label] [[:project "this project"] [:assets "all assets"]]]
           ^{:key (str k)}
           [:button.tab {:class (when (= k @scope) "on")
                         :on-click #(reset! scope k)}
            label
            ;; Only while searching, and only on the tab you are not looking at:
            ;; it exists to say "the thing you asked for is over here".
            (when (and searching? (not= k @scope))
              [:span.count (get tallies k)])]))]
       [:div.pane-body
        (if (= :project @scope) [this-project ctx] [all-assets ctx])
        (when status [:div.dim status])]
       ;; At the foot, where a list's own "add one" goes, and pinned there so it
       ;; does not scroll away under a long one.
       [:div.pool-foot
        [:button {:title "a new empty symbol in this project's library"
                  :on-click (fn [_]
                              (let [sid (clip/fresh-id document)]
                                (rf/dispatch [::project/add-symbol sid])
                                (rf/dispatch [::ui/select [:symbol sid]])
                                ;; Show its row and open its name, so what it
                                ;; is called is the next thing you type.
                                (reset! scope :project)
                                (reset! query "")
                                (reset! editing [:symbol sid])))}
         "+ symbol"]
        [:button {:title "add a video, a sound or an image — or drop one on this pane"
                  :disabled loading?
                  :on-click #(.click (js/document.getElementById "pool-file"))}
         "upload…"]]])))
